import { Text, View } from "@react-pdf/renderer";
import React from "react";
import { borderStyleGray } from "../reportingHelpers";
import { OfficialCell } from "./Officials";

const cellStyle = {
  border: borderStyleGray,
  borderLeft: "unset",
  borderTop: "unset",
  alignItems: "center",
};

const RosterHeader = ({ intl }) => (
  <View style={{ flexDirection: "row", fontSize: 7, width: "100%" }}>
    <View style={{ ...cellStyle, flexBasis: "10%" }}>
      <Text>
        {intl.formatMessage({
          id: "reports.minute",
          defaultMessage: "Minute",
        })}
      </Text>
    </View>
    <View style={{ ...cellStyle, flexBasis: "10%" }}>
      <Text>
        {intl.formatMessage({
          id: "reports.matchReport.shirtNumber",
          defaultMessage: "Shirt #",
        })}
      </Text>
    </View>
    <View style={{ ...cellStyle, flexBasis: "50%" }}>
      <Text>
        {intl.formatMessage({
          id: "reports.name",
          defaultMessage: "Name",
        })}
      </Text>
    </View>
    <View
      style={{
        ...cellStyle,
        flexBasis: "10%",
        color: "#FFFFFF",
        backgroundColor: "#006300",
      }}
    >
      <Text>
        {intl.formatMessage({
          id: "reports.matchReport.greenCard",
          defaultMessage: "Green",
        })}
      </Text>
    </View>
    <View
      style={{
        ...cellStyle,
        flexBasis: "10%",
        backgroundColor: "#FBFB02",
      }}
    >
      <Text>
        {intl.formatMessage({
          id: "reports.matchReport.yellowCard",
          defaultMessage: "Yellow",
        })}
      </Text>
    </View>
    <View
      style={{
        ...cellStyle,
        flexBasis: "10%",
        color: "#FFFFFF",
        backgroundColor: "#FF2500",
      }}
    >
      <Text>
        {intl.formatMessage({
          id: "reports.matchReport.redCard",
          defaultMessage: "Red",
        })}
      </Text>
    </View>
  </View>
);

const EmptyCardCell = () => (
  <View style={{ ...cellStyle, flexBasis: "10%" }}></View>
);

const buildCardMinuteStr = (cards, type) =>
  cards
    ? cards
        .filter((c) => c.type === type)
        .map((c) => c.minute)
        .join(",")
    : null;

const RosterRow = ({
  cards,
  minuteIn,
  starter,
  bib = " ", // empty string so the the row won't collapse on empty rows
  personFullName,
  position,
  captain,
}) => {
  const greenCard = buildCardMinuteStr(cards, "greenCard");
  const yellowCard = buildCardMinuteStr(cards, "yellowCard");
  const redCard = buildCardMinuteStr(cards, "redCard");
  let personFullNameLabel = personFullName;
  if (position === "GK") personFullNameLabel = `${personFullNameLabel} (GK)`;
  if (captain) personFullNameLabel = `${personFullNameLabel} (C)`;
  return (
    <View style={{ flexDirection: "row", width: "100%" }}>
      <View style={{ ...cellStyle, flexBasis: "10%" }}>
        <Text style={{ maxLines: 1, textOverflow: "ellipsis" }}>
          {starter ? "X" : minuteIn}
        </Text>
      </View>
      <View style={{ ...cellStyle, flexBasis: "10%" }}>
        <Text>{bib}</Text>
      </View>
      <View
        style={{
          ...cellStyle,
          flexBasis: "50%",
          alignItems: "flex-start",
          paddingLeft: 2,
        }}
      >
        <Text>{personFullNameLabel}</Text>
      </View>
      {greenCard ? (
        <View
          style={{
            ...cellStyle,
            flexBasis: "10%",
            color: "#FFFFFF",
            backgroundColor: "#006300",
          }}
        >
          <Text>{greenCard}</Text>
        </View>
      ) : (
        <EmptyCardCell />
      )}
      {yellowCard ? (
        <View
          style={{
            ...cellStyle,
            flexBasis: "10%",
            backgroundColor: "#FBFB02",
          }}
        >
          <Text>{yellowCard}</Text>
        </View>
      ) : (
        <EmptyCardCell />
      )}
      {redCard ? (
        <View
          style={{
            ...cellStyle,
            flexBasis: "10%",
            color: "#FFFFFF",
            backgroundColor: "#FF2500",
          }}
        >
          <Text>{redCard}</Text>
        </View>
      ) : (
        <EmptyCardCell />
      )}
    </View>
  );
};

const TeamRoster = ({ cards, intl, roster, subs, fixtureProfile, roles }) => {
  const maxRosterSize = Number(fixtureProfile?.maximumRosterSize) || 18;
  const emptyRosterSlots = Math.max(0, maxRosterSize - roster.length);
  const rosterData = [
    ...roster,
    ...Array.from({ length: emptyRosterSlots }),
  ];
  const getCoach = () => {
    const coach = Object.values(roles).find((r) => r.role === "COACH_HEAD");
    return coach ? coach.name : "";
  };
  const getTeamManager = () => {
    const manager = Object.values(roles).find((r) => r.role === "MANAGER");
    return manager ? manager.name : "-";
  };
  return (
    <View
      style={{
        fontSize: 10,
        border: borderStyleGray,
        borderRight: "unset",
        borderBottom: "unset",
        flexBasis: "49%",
        marginTop: 6,
      }}
    >
      <RosterHeader intl={intl} />
      {rosterData.map((player, index) => (
        <RosterRow
          {...player}
          minuteIn={subs?.[player?.personId]}
          cards={cards?.[player?.personId]}
          key={index}
        />
      ))}
      <View style={{ flexDirection: "row", width: "100%" }}>
        <View style={{ ...cellStyle, flexBasis: "20%" }}>
          <Text>Coach</Text>
        </View>
        <View
          style={{
            ...cellStyle,
            flexBasis: "80%",
            alignItems: "flex-start",
            paddingLeft: 2,
          }}
        >
          <Text>{getCoach()}</Text>
        </View>
      </View>
      <OfficialCell role="Team Manager" name={getTeamManager()} />
    </View>
  );
};

export default TeamRoster;
